import { ChangeDetectorRef, Directive, DoCheck, inject, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive()
export abstract class DsAbstractControlComponent implements ControlValueAccessor, DoCheck, OnDestroy {
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);

  protected readonly subscriptions: Subscription = new Subscription();

  /** Last error state pushed to the view, so `ngDoCheck` only marks it dirty on a real change. */
  private hasErrorState = false;

  /** Placeholder text */
  @Input() placeholder = '';

  /** Optional label */
  @Input() label = '';

  /** HTML id attribute */
  @Input() id?: string;

  /** Optional ARIA label for accessibility */
  @Input() ariaLabel?: string;

  /** Optional ARIA labelledby for accessibility */
  @Input() ariaLabelledby?: string;

  /**
   * `markAsTouched()` emits neither `valueChanges` nor `statusChanges`, and these controls are
   * OnPush, so Material never gets a chance to pick a new error state up on its own: the view is
   * skipped and the `MatFormFieldControl` inside it never runs its own `ngDoCheck`. This hook is
   * called from the parent view instead, so it can dirty this one and let the error border, icon
   * and message render in the same tick — which is what makes a programmatic form validation
   * (clicking Save on untouched fields) visible.
   */
  ngDoCheck(): void {
    const hasErrorState: boolean = !!this.control?.invalid && (this.control.touched || this.control.dirty);

    if (hasErrorState !== this.hasErrorState) {
      this.hasErrorState = hasErrorState;
      this.changeDetectorRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onChange: any = () => {};

  onTouched: any = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Keydown handler for the in-panel search field.
   *
   * Stops propagation so the host MatSelect's typeahead does not hijack typing, but lets Escape
   * bubble through so the overlay still closes on Esc.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      return;
    }

    event.stopPropagation();
  }

  abstract writeValue(obj: any): void;

  abstract setDisabledState?(isDisabled: boolean): void;

  abstract get control(): FormControl;
}
